import { useAccount } from 'wagmi';

import useLentLoans from '@/hooks/query/loans/useLentLoans';
import useLoansOffers from '@/hooks/query/loans/useLoansOffers';
import useWalletAllNFTs from '@/hooks/query/useWalletAllNFTs';
import useWalletCryptos from '@/hooks/query/useWalletCrypto';
import { useAppSelector } from '@/hooks/redux';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/helper';

import ConnectionButton from './ConnectionButton';
import LogoutButton from './LogoutButton';
import UserStatsList from './UserStatsList';
import { WalletInfoAlternate } from '../../widgets/WalletInfo';

interface RightSidebarAccountSettingsProps {
  setInnerTab: (tab: 'Account' | 'Notifications' | 'Menu' | null) => void;
}

const RightSidebarAccountSettings: React.FC<RightSidebarAccountSettingsProps> = ({ setInnerTab }) => {
  const { mode } = useTheme();
  const { topBanner } = useAppSelector((state) => state.modal);
  const { userDetail } = useAppSelector((state) => state.user);
  const { data: walletCrypto } = useWalletCryptos();
  const { data: walletValuation } = useWalletAllNFTs({ cachedData: true });
  const nettyWorth = (Number(walletCrypto?.walletCryptoBalance) || 0) + (walletValuation || 0);
  const { address } = useAccount();

  const { data: offersLoans, isLoading: offersLoansLoading } = useLoansOffers(address);
  const { data: lentLoans, isLoading: lentLoansLoading } = useLentLoans(address);

  return (
    <aside className={cn('flex h-full max-lg:border-t flex-col gap-3 ss:gap-4 lg:gap-8 pt-3 ss:pt-6 lg:pt-4', topBanner && 'mt-10', mode === 'dark' ? 'border-coal-100' : '')}>
      <WalletInfoAlternate />

      {userDetail && (
        <UserStatsList
          nettyWorth={nettyWorth}
          nettyScore={Number(userDetail.nettyScore)}
          customFields={[
            {
              id: 3,
              txt: 'Active Loans',
              value: userDetail?.activeCount ?? 0,
              url: '/loans/my-loans',
            },
            {
              id: 4,
              txt: 'Loan Offers',
              value: offersLoansLoading ? <div className='loader'></div> : (offersLoans?.length ?? 0),
              url: '/loans/my-loans',
            },
            {
              id: 5,
              txt: 'Lending',
              value: lentLoansLoading ? <div className='loader'></div> : lentLoans?.length || 0,
              url: '/loans/my-loans?tab=lent',
            },
          ]}
        />
      )}

      <div className={cn(mode === 'dark' ? 'border-coal-100' : '!bg-light-blue-grey', 'flex flex-col gap-3 ss:gap-4 lg:gap-3 pb-4 ss:pb-6 lg:pb-0')}>
        <button
          type='button'
          onClick={() => {
            setInnerTab('Menu');
          }}
          className='text-primary dark:text-primary-blue-100 bg-primary/15 max-lg:border border-coal-100 py-3 px-6 ss:text-base/5 text-sm font-medium rounded-[4px] hover:opacity-75 active:scale-95'
        >
          Menu
        </button>

        <div className='h-[1px] lg:hidden block !border-y dark:!border-t-coal-100'></div>

        <ConnectionButton />
        <LogoutButton />
      </div>
    </aside>
  );
};

export default RightSidebarAccountSettings;
